<template>
  <Teleport to="body">
    <div class="dialog-overlay" @click.self="cancel">
      <div class="dialog-panel" role="dialog" aria-modal="true" :aria-label="$t('imagePicker.title')">
        <div class="dialog-header">
          <h2>{{ $t('imagePicker.title') }}</h2>
          <button
            ref="closeBtn"
            type="button"
            class="dialog-close"
            :aria-label="$t('imagePicker.close')"
            @click="cancel"
          >×</button>
        </div>

        <div class="dialog-search">
          <input
            type="text"
            v-model="query"
            :placeholder="$t('imagePicker.searchPlaceholder')"
          />
        </div>

        <div class="dialog-body">
          <p v-if="!hasAnyImages" class="empty-state">{{ $t('imagePicker.noImages') }}</p>
          <p v-else-if="filtered.length === 0" class="empty-state">{{ $t('imagePicker.noMatches') }}</p>
          <div v-else class="thumb-grid">
            <button
              v-for="img in filtered"
              :key="img.id"
              type="button"
              class="thumb-cell"
              :class="{ selected: selectedId === img.id }"
              :title="labelFor(img)"
              @click="selectedId = img.id"
              @dblclick="confirm"
            >
              <img
                :src="imageUrl(img)"
                :alt="altTextFor(img, currentLocale)"
                @error="onImgError($event, img, currentLocale)"
              />
              <div class="thumb-label">
                <span class="thumb-id">{{ formatId(img.id) }}</span>
                <span class="thumb-text">{{ labelFor(img) }}</span>
              </div>
            </button>
          </div>
        </div>

        <div class="dialog-footer">
          <button type="button" class="btn btn-secondary" @click="cancel">
            {{ $t('imagePicker.cancel') }}
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="selectedId == null"
            @click="confirm"
          >
            {{ $t('imagePicker.confirm') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import i18n from '@/i18n';
import { imageUrl, pickLocaleString, altTextFor, onImgError } from '@/services/imageHelpers';

export default {
  name: 'ImagePicker',
  props: {
    // Map<id, ImageRecord> shared from the parent (Card Editor).
    // TODO: when more components consume this cache, promote it to a shared
    // composable / store rather than passing it as a prop.
    imageCache: { type: Map, required: true },
    // The slot being filled (e.g. 'image1' / 'image2'). Echoed back on emit
    // so the parent knows where to put the chosen id.
    target: { type: String, default: null },
    // Optional: pre-select an existing image when "Change image" is clicked.
    initialImageId: { type: Number, default: null },
  },
  emits: ['picked', 'cancel'],
  data() {
    return {
      query: '',
      selectedId: this.initialImageId,
      previouslyFocusedElement: null,
    };
  },
  computed: {
    currentLocale() {
      return i18n.global.locale.value;
    },
    images() {
      // Newest first by id, matching the Image Browser's ordering.
      return Array.from(this.imageCache.values()).sort((a, b) => b.id - a.id);
    },
    hasAnyImages() {
      return this.images.length > 0;
    },
    filtered() {
      const q = this.query.trim().toLowerCase();
      if (!q) return this.images;
      return this.images.filter(img => {
        const fields = [img.label_en, img.label_de, img.label_fr, img.label_it];
        return fields.some(s => s && s.toLowerCase().includes(q));
      });
    },
  },
  mounted() {
    // Save the originally-focused element so we can restore focus on close
    // (typically the "Pick image" / "Change image" button in the Card Editor).
    this.previouslyFocusedElement = document.activeElement;
    document.addEventListener('keydown', this.onKeydown);
    this.$nextTick(() => {
      if (this.$refs.closeBtn) this.$refs.closeBtn.focus();
    });
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown);
    const el = this.previouslyFocusedElement;
    if (el && typeof el.focus === 'function') {
      el.focus();
    }
  },
  methods: {
    imageUrl,
    altTextFor,
    onImgError,
    formatId(id) {
      return `#${String(id).padStart(4, '0')}`;
    },
    labelFor(img) {
      return pickLocaleString(img, 'label', this.currentLocale)
        || this.$t('imageBrowser.noLabel');
    },
    onKeydown(e) {
      if (e.key === 'Escape') this.cancel();
    },
    cancel() {
      this.$emit('cancel');
    },
    confirm() {
      if (this.selectedId == null) return;
      this.$emit('picked', this.selectedId);
    },
  },
};
</script>

<style scoped>
.dialog-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
  font-family: 'Montserrat', 'Avenir', Helvetica, Arial, sans-serif;
}

.dialog-panel {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  max-width: 880px;
  width: 100%;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header */

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e9ecef;
}

.dialog-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
}

.dialog-close {
  width: 32px;
  height: 32px;
  background: none;
  border: none;
  font-size: 24px;
  line-height: 1;
  color: #6c757d;
  cursor: pointer;
  border-radius: 4px;
  transition: color 0.15s ease, background-color 0.15s ease;
}
.dialog-close:hover { color: #2c3e50; background: #f1f3f5; }
.dialog-close:focus-visible { outline: 2px solid #42b983; outline-offset: 2px; }

/* Search */

.dialog-search {
  padding: 12px 20px;
  border-bottom: 1px solid #e9ecef;
}

.dialog-search input {
  font-family: inherit;
  font-size: 14px;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  background-color: #fff;
  color: #2c3e50;
  width: 100%;
  height: 38px;
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.dialog-search input:focus {
  outline: none;
  border-color: #42b983;
  box-shadow: 0 0 0 3px rgba(66, 185, 131, 0.18);
}

/* Body */

.dialog-body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 16px 20px;
  background: #fdfdfd;
}

.empty-state {
  margin: 32px 0;
  text-align: center;
  color: #6c757d;
  font-size: 14px;
}

/* Thumbnail grid */

.thumb-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
}

.thumb-cell {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  padding: 0;
  font-family: inherit;
  text-align: left;
  color: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
}

.thumb-cell:hover {
  border-color: #b3e0c7;
  background: #fafffc;
}

.thumb-cell.selected {
  border-color: #42b983;
  box-shadow: 0 0 0 2px rgba(66, 185, 131, 0.25);
  background: #eafaf1;
}

.thumb-cell:focus-visible {
  outline: 2px solid #42b983;
  outline-offset: 2px;
}

.thumb-cell img {
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  display: block;
}

.thumb-label {
  padding: 8px 10px 10px 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 13px;
  min-width: 0;
}

.thumb-id {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 11px;
  color: #adb5bd;
  letter-spacing: 0.5px;
}

.thumb-text {
  color: #2c3e50;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Footer */

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid #e9ecef;
  background: #fff;
}

.btn {
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  padding: 9px 20px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
  letter-spacing: 0.3px;
}
.btn:disabled { cursor: not-allowed; opacity: 0.5; }

.btn-primary { background: #42b983; color: #fff; }
.btn-primary:hover:not(:disabled) { background: #36a273; }

.btn-secondary {
  background: #fff;
  color: #2c3e50;
  border: 1px solid #ced4da;
}
.btn-secondary:hover:not(:disabled) { background: #f1f3f5; }

/* Responsive */

@media (max-width: 600px) {
  .dialog-overlay { padding: 8px; }
  .dialog-panel { max-height: 95vh; }
  .thumb-grid { grid-template-columns: repeat(2, 1fr); }
  .dialog-footer { flex-direction: column-reverse; }
  .dialog-footer .btn { width: 100%; }
}
</style>
